import {isFeatureEnabled} from 'feature-flags';
import {
  Card,
  closeCurrentDialog,
  DataStatus,
  FormSubmitHandler,
  openDeleteDialog,
  openDialog,
  Parameter,
  showNotification,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import {formatPhoneNumber} from 'platform/locale';

// eslint-disable-next-line no-restricted-imports -- Intended
import {always, ifElse, pathOr, pipe, sortBy} from 'ramda';
import {isNilOrEmpty, isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  PersonResponseBodyV2,
  useCreateCustomerContactV2Mutation,
  useDeleteCustomerContactMutation,
  useGetConsentListQuery,
  useGetCustomerV2Query,
  usePatchCustomerContactV2Mutation,
  useSetPrimaryCustomerContactMutation,
} from '@dms/api/customer';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {ApiError, buildArray, suffixTestId, TestIdProps} from 'shared/web';

import {useCustomerPersonAttributes} from '../../hooks/useCustomerPersonAttributes';
import {usePermissions} from '../../hooks/usePermissions/usePermissions';
import {getContactPersonRoleTypeName} from '../../utils/getContactPersonRoleTypeName';
import {getContactRequestBody} from '../../utils/getContactRequestBody';
import {getEmailParameter} from '../../utils/getEmailParameter';
import {getNaturalPersonFullName} from '../../utils/getNaturalPersonFullName';
import {handleApiError} from '../../utils/handleApiError';
import {handleContactApiError} from '../../utils/handleContactApiError';
import {ContactPersonForm, ContactPersonFormValues} from '../ContactPersonForm/ContactPersonForm';
import {InfoCard} from '../InfoCard/InfoCard';
import {ContactPersonConsentFlag} from './components/ContactPersonConsentFlag';

interface CustomerContactPersonCardProps extends TestIdProps {
  customerId: string;
  isEditingDisabled?: boolean;
}

export function CustomerContactPersonCard(props: CustomerContactPersonCardProps) {
  const [getAttributes] = useCustomerPersonAttributes();

  const {data, isLoading, isError} = useGetCustomerV2Query({customerId: props.customerId});
  const [canReadConsents] = usePermissions({
    permissionKeys: ['readGdprConsentDocument'],
    scopes: {
      readGdprConsentDocument: {
        // Customer has no branchId
        branchId: 'SKIP',
      },
    },
  });
  const {currentData: consents} = useGetConsentListQuery(
    {customerId: props.customerId},
    {skip: !canReadConsents}
  );
  const [createContact, {isLoading: isCreateContactLoading}] = useCreateCustomerContactV2Mutation();
  const [editContact, {isLoading: isEditContactLoading}] = usePatchCustomerContactV2Mutation();
  const [deleteContact, {isLoading: isDeleteContactLoading}] = useDeleteCustomerContactMutation();
  const [setPrimaryContact, {isLoading: isSetPrimaryContactLoading}] =
    useSetPrimaryCustomerContactMutation();

  const setContactAsPrimary = (contactId: string) =>
    setPrimaryContact({customerId: props.customerId, contactId})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.success'));
        closeCurrentDialog();
      })
      .catch(handleApiError);

  const onCreateSubmit: FormSubmitHandler<ContactPersonFormValues> = async (values, setErrors) => {
    const {isPrimary, ...person} = values;
    const isFirstContact = isNilOrEmpty(data?.contacts);
    const {personRequestBody, emailFormIndexes} = getContactRequestBody(person);

    await createContact({customerId: props.customerId, personRequestBody})
      .unwrap()
      .then((createdContact) => {
        if (isPrimary && !isFirstContact) {
          return setContactAsPrimary(createdContact.id);
        }
        showNotification.success(i18n.t('general.notifications.success'));
        closeCurrentDialog();
      })
      .catch((error: ApiError) => handleContactApiError(error, setErrors, emailFormIndexes));
  };

  const onEditSubmit =
    (contactId: string, wasPrimary: boolean): FormSubmitHandler<ContactPersonFormValues> =>
    async (values, setErrors) => {
      const {isPrimary, ...person} = values;
      const {personRequestBody, emailFormIndexes} = getContactRequestBody(person);

      await editContact({customerId: props.customerId, contactId, personRequestBody})
        .unwrap()
        .then(() => {
          if (isPrimary && !wasPrimary) {
            return setContactAsPrimary(contactId);
          }
          showNotification.success(i18n.t('general.notifications.success'));
          closeCurrentDialog();
        })
        .catch((error: ApiError) => handleContactApiError(error, setErrors, emailFormIndexes));
    };

  const onDelete = (contactId: string) =>
    openDeleteDialog({
      onConfirm: () =>
        deleteContact({
          customerId: props.customerId,
          contactId,
        })
          .unwrap()
          .catch(handleApiError),
    });

  const getPhoneNumber = pipe(
    pathOr(null, ['phoneNumbers', 0]),
    ifElse(
      isNotNilOrEmpty,
      (value) => formatPhoneNumber(`${value?.prefix}${value?.number}`),
      always(i18n.t('entity.customer.labels.noPhoneNumber'))
    )
  );

  const getEmail = pipe(
    pathOr(null, ['emails', 0]),
    ifElse(
      isNotNilOrEmpty,
      (value) => getEmailParameter(value.email),
      always(i18n.t('entity.customer.labels.noEmail'))
    )
  );

  const isPrimaryContactEnabled = isFeatureEnabled(featureFlags.CUSTOMER_PRIMARY_CONTACT);

  const sortedContacts = isPrimaryContactEnabled
    ? sortBy(
        (contact: PersonResponseBodyV2) => contact.id !== data?.primaryContactId,
        data?.contacts ?? []
      )
    : (data?.contacts ?? []);

  return (
    <Card
      title={i18n.t('entity.customer.labels.contactPersons')}
      actions={[
        {
          type: 'button',
          variant: 'link',
          leftIcon: 'content/add_circle',
          isDisabled: isLoading || props.isEditingDisabled,
          tooltip: props.isEditingDisabled ? i18n.t('general.labels.noPermission') : undefined,
          title: i18n.t('general.actions.add'),
          onClick: () =>
            openDialog(
              <ContactPersonForm
                customerId={props.customerId}
                addresses={data?.addresses}
                isLoading={isCreateContactLoading || isSetPrimaryContactLoading}
                isPrimary={isNilOrEmpty(data?.contacts)}
                onSubmit={onCreateSubmit}
                data-testid={suffixTestId('createContact', props)}
              />,
              {
                title: i18n.t('entity.customer.actions.newContactPerson'),
                withAdditionalFooter: true,
                scrollBehavior: 'inside',
                'data-testid': suffixTestId('createContact', props),
              }
            ),
        },
      ]}
      data-testid={props['data-testid']}
    >
      <VStack spacing={4}>
        <DataStatus
          isLoading={isLoading}
          isError={isError}
          isEmpty={!data?.contacts?.length}
          emptyMessage={i18n.t('general.labels.noData')}
          emptySubheadline={i18n.t('entity.customer.labels.noContact')}
          minHeight={23}
        >
          <VStack spacing={4}>
            {sortedContacts.map((contact: PersonResponseBodyV2, index) => (
              <InfoCard
                key={contact.id}
                subTitle={contact.roles}
                title={getNaturalPersonFullName(contact)}
                isPrimary={isPrimaryContactEnabled && contact.id === data?.primaryContactId}
                flags={
                  isNotNil(contact.roleType)
                    ? [
                        {
                          label: getContactPersonRoleTypeName(i18n, contact.roleType) ?? '',
                          colorScheme: 'neutral',
                          isSubtle: true,
                        },
                      ]
                    : undefined
                }
                titleSuffix={
                  isNotNil(consents) ? (
                    <ContactPersonConsentFlag
                      contactPersonId={contact.id}
                      consents={consents}
                      data-testid={suffixTestId(`contact-${index}`, props)}
                    />
                  ) : undefined
                }
                description={buildArray<Parameter>()
                  .add(getPhoneNumber(contact))
                  .add(getEmail(contact))}
                actions={[
                  {
                    type: 'iconButtonWithTooltip',
                    severity: 'danger',
                    icon: 'action/delete',
                    isDisabled:
                      isDeleteContactLoading ||
                      (data?.discriminator === 'BUSINESS' && data?.contacts?.length === 1) ||
                      props.isEditingDisabled,
                    tooltip: {
                      label: props.isEditingDisabled
                        ? i18n.t('general.labels.noPermission')
                        : undefined,
                    },
                    onClick: () => onDelete(contact.id),
                  },
                  {
                    type: 'iconButtonWithTooltip',
                    icon: 'image/edit',
                    isDisabled: props.isEditingDisabled,
                    tooltip: {
                      label: props.isEditingDisabled
                        ? i18n.t('general.labels.noPermission')
                        : undefined,
                    },
                    onClick: () =>
                      openDialog(
                        <ContactPersonForm
                          customerId={props.customerId}
                          addresses={data?.addresses}
                          isLoading={isEditContactLoading || isSetPrimaryContactLoading}
                          contact={contact}
                          isPrimary={contact.id === data?.primaryContactId}
                          onSubmit={onEditSubmit(contact.id, contact.id === data?.primaryContactId)}
                          data-testid={suffixTestId('editContact', props)}
                        />,
                        {
                          title: i18n.t('entity.customer.labels.editContactPerson'),
                          scrollBehavior: 'inside',
                          withAdditionalFooter: true,
                          'data-testid': suffixTestId('editContact', props),
                        }
                      ),
                  },
                ]}
                attributes={getAttributes(contact, {
                  isPrimary: isPrimaryContactEnabled
                    ? contact.id === data?.primaryContactId
                    : undefined,
                })}
                data-testid={suffixTestId(`contact-${index}`, props)}
                shouldShowActionParameters
                customerId={data?.id}
                selectedContactId={contact.id}
              />
            ))}
          </VStack>
        </DataStatus>
      </VStack>
    </Card>
  );
}
